import type { CharacterConfig, MatchHistoryEntry, MeProfile } from '@kokun/shared';
import { safeStorage } from './store';

const TOKEN_KEY = 'kokun.token';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Cliente HTTP. El token de sesión lo emite el servidor; aquí solo se guarda para
 * presentarlo (es la única prueba de identidad, no un ID inventado por el navegador).
 */
export const api = {
  get token(): string | null {
    return safeStorage.get(TOKEN_KEY);
  },
  set token(t: string | null) {
    if (t) safeStorage.set(TOKEN_KEY, t);
    else safeStorage.remove(TOKEN_KEY);
  },

  async req<T>(method: string, path: string, body?: unknown, extra: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = { ...((extra.headers as Record<string, string>) ?? {}) };
    if (this.token) headers.authorization = `Bearer ${this.token}`;
    let payload: BodyInit | undefined;
    if (body instanceof Blob) payload = body;
    else if (body !== undefined) {
      headers['content-type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    const res = await fetch(path, { ...extra, method, headers, body: payload });
    if (res.status === 204) return undefined as T;
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Error de conexión');
    return data as T;
  },

  async guest(name: string) {
    const r = await this.req<{ token: string; profile: MeProfile }>('POST', '/api/auth/guest', { name });
    this.token = r.token;
    return r.profile;
  },
  async login(username: string, password: string) {
    const r = await this.req<{ token: string; profile: MeProfile }>('POST', '/api/auth/login', { username, password });
    this.token = r.token;
    return r.profile;
  },
  register: (username: string, password: string) => api.req<{ profile: MeProfile }>('POST', '/api/auth/register', { username, password }),
  async logout() {
    await this.req('POST', '/api/auth/logout').catch(() => undefined);
    this.token = null;
  },
  me: () => api.req<MeProfile>('GET', '/api/me'),
  rename: (name: string) => api.req<MeProfile>('PATCH', '/api/me', { name }),
  saveCharacter: (character: CharacterConfig) => api.req<MeProfile>('PUT', '/api/me/character', { character }),
  resetCharacter: () => api.req<MeProfile>('DELETE', '/api/me/character'),
  deleteAccount: () => api.req<void>('DELETE', '/api/me'),
  history: () => api.req<MatchHistoryEntry[]>('GET', '/api/me/history'),
  uploadFace: (blob: Blob) => api.req<{ faceRev: number }>('PUT', '/api/me/face', blob, { headers: { 'x-face-consent': 'granted', 'content-type': blob.type } }),
  deleteFace: () => api.req<void>('DELETE', '/api/me/face'),
  health: () => fetch('/api/health').then((r) => r.ok),

  /** Descarga privada de un rostro (requiere compartir sala con su dueño). */
  async face(playerId: string): Promise<Blob | null> {
    if (!this.token) return null;
    const res = await fetch(`/api/faces/${playerId}`, { headers: { authorization: `Bearer ${this.token}` } });
    return res.ok ? res.blob() : null;
  },
};
